import { useState, type FormEvent, type ReactNode } from "react";
import type { Role } from "@sts/schemas";
import { ApiError, ApiUnreachableError } from "../api/client.js";
import type { OpenInvitation, OpenPasswordReset } from "../api/types.js";
import { Async } from "../components/Async.jsx";
import { IconScannix } from "../components/Icons.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href, useNavigate } from "../app/router.js";
import { longDate } from "../lib/format.js";

/**
 * The two screens somebody reaches from an email, holding a link and nothing
 * else: "choose a password and join this company", and "choose a new password
 * because you forgot the old one". Plus the little form on the sign-in card
 * that asks for the second link.
 *
 * All three are shown to people with NO SESSION — and, in the invitation case,
 * no account at all: following the link is what creates one. `App` therefore
 * checks for these routes before it decides whether to show the sign-in screen,
 * or an emailed invitation would land on a sign-in form asking for a password
 * the person has never had.
 *
 * THREE RULES, each of them a thing that goes wrong in products that get this
 * wrong, and each of them ultimately enforced by the API rather than here:
 *
 *  - THE API'S WORDS, NOT OURS, for every refusal. A dead link and a weak
 *    password both come back with a sentence written for the person reading it,
 *    and rewriting either here would mean two places to keep true. See
 *    `InvitationUnusableError`, `ResetLinkUnusableError` and
 *    `assertUsablePassword` in the API.
 *  - NOTHING SAYS WHETHER AN ACCOUNT EXISTS. `POST /auth/password-resets`
 *    answers 202 with one message whether or not the address is known, and this
 *    screen shows that message verbatim — a confirmation of our own composing
 *    would be the place the two cases started to differ.
 *  - THE TOKEN GOES NOWHERE BUT THE REQUEST. It is in the URL fragment, which
 *    the browser never sends to a server, and it is never written into a link,
 *    a heading or a message on any of these screens.
 */

/** Matches the Team screen's vocabulary; the invitee reads the same words. */
const ROLE_LABEL: Record<Role, string> = {
  owner: "Owner",
  admin: "Administrator",
  office: "Office / Sales",
  surveyor: "Surveyor",
  designer: "Designer",
  "technical-supervisor": "Technical supervisor",
  installer: "Installer",
  auditor: "Auditor (read only)",
};

/**
 * What the API asks of a password, said before it is typed rather than after.
 *
 * The number is the API's `MIN_PASSWORD_LENGTH`. It is repeated here rather than
 * imported because `@sts/api` is not a dependency of the portal bundle — if the
 * two ever disagree the API wins, and its refusal is what the reader is shown.
 */
const PASSWORD_HINT =
  "At least 12 characters. A short phrase you can remember works well, and is " +
  "harder to guess than a short password with symbols in it.";

/** The card every one of these screens sits in — the sign-in card's twin. */
function LinkCard({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <main className="signin">
      <div className="signin__card">
        <span className="signin__mark" aria-hidden="true">
          <IconScannix />
        </span>
        <h1 className="wordmark">Scannix</h1>
        <p className="lede">{title}</p>
        {children}
      </div>
    </main>
  );
}

/**
 * The message to print for a failed write, in the API's own words where it has
 * any.
 *
 * Deliberately not `Failure` from `components/Async.jsx`: that component is
 * built for a screen inside the application, and talks about roles and grants
 * and reloading. The person reading this one has no account yet.
 */
function messageFor(error: unknown): string {
  if (error instanceof ApiUnreachableError) {
    return "The API did not answer. Check your connection and try again — nothing has been changed.";
  }
  if (error instanceof ApiError) return error.message;
  return "That did not work. Try again.";
}

/**
 * Choose a password, twice.
 *
 * The confirmation field is not ceremony: this is the only moment the password
 * is ever typed, nobody else can look it up afterwards, and a typo here means a
 * second reset link. The two are compared before anything is sent, so a mistyped
 * confirmation does not spend a single-use token.
 */
function ChoosePassword({
  submitLabel,
  busyLabel,
  onChosen,
}: {
  readonly submitLabel: string;
  readonly busyLabel: string;
  readonly onChosen: (password: string) => Promise<void>;
}): ReactNode {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = again.length > 0 && again !== password;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    if (password !== again) {
      setError("The two passwords are not the same. Type the same one twice.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onChosen(password);
      // Deliberately no `setBusy(false)` on success: the caller navigates or
      // swaps the screen, and re-enabling the button would offer a second
      // submission of a link that has just been spent.
    } catch (cause: unknown) {
      setError(messageFor(cause));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <div className="field">
        <label htmlFor="new-password">Choose a password</label>
        <input
          id="new-password"
          name="new-password"
          type={visible ? "text" : "password"}
          autoComplete="new-password"
          required
          minLength={12}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <p className="hint small">{PASSWORD_HINT}</p>
      </div>

      <div className="field">
        <label htmlFor="new-password-again">Type it again</label>
        <input
          id="new-password-again"
          name="new-password-again"
          type={visible ? "text" : "password"}
          autoComplete="new-password"
          required
          value={again}
          onChange={(e) => setAgain(e.target.value)}
        />
        <button
          type="button"
          className="link small"
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? "Hide password" : "Show password"}
        </button>
        {mismatch ? (
          <p className="hint small">
            The two passwords are not the same yet.
          </p>
        ) : null}
      </div>

      {error ? (
        <div className="notice fail" role="alert">
          <p>{error}</p>
        </div>
      ) : null}

      <button
        type="submit"
        disabled={busy || password.length === 0 || password !== again}
      >
        {busy ? busyLabel : submitLabel}
      </button>
    </form>
  );
}

/**
 * "You have been invited to join a company — choose a password."
 *
 * On success the API answers with a session, so this signs them straight in
 * rather than sending them to a sign-in form: they have just proved they hold
 * the mailbox AND chosen the password, which is strictly more than signing in
 * asks for. The navigation away from this route matters — staying on
 * `#/invitation/<token>` would re-open a link that has just been spent, and
 * show the invitee a "no longer valid" page immediately after it worked.
 */
export function AcceptInvitation({
  token,
}: {
  readonly token: string;
}): ReactNode {
  const { client, session } = usePortal();
  const navigate = useNavigate();

  const { state, reload } = useAsync<OpenInvitation>(
    () => client.openInvitation(token),
    [client, token],
  );

  return (
    <LinkCard title="You have been invited">
      <Async state={state} what="this invitation" reload={reload}>
        {(invitation) => (
          <>
            <p>
              <strong>{invitation.organisationName}</strong> has invited you to
              join them on Scannix
              {invitation.invitedByName
                ? ` — the invitation was sent by ${invitation.invitedByName}`
                : ""}
              . You will be a{" "}
              <strong>{ROLE_LABEL[invitation.role].toLowerCase()}</strong>.
            </p>
            <p className="small">
              You will sign in as{" "}
              <span className="mono">{invitation.email}</span>. Choose a password
              now — nobody else sees it, not whoever invited you and not anybody
              else at {invitation.organisationName}. This link works once and
              stops working on {longDate(invitation.expiresAt)}.
            </p>

            <ChoosePassword
              submitLabel="Join and sign in"
              busyLabel="Joining…"
              onChosen={async (password) => {
                const result = await client.acceptInvitation(token, password);
                // Away from the spent link FIRST, so the screen this replaces
                // is never re-rendered against a token that is now dead.
                navigate(href.surveys());
                session.set({
                  token: result.token,
                  expiresAt: result.expiresAt,
                  email: invitation.email,
                  // A brand new account belongs to exactly one organisation,
                  // so the API resolves it without being told which.
                  organisationId: null,
                });
              }}
            />
          </>
        )}
      </Async>
    </LinkCard>
  );
}

/**
 * "Choose a new password" — the screen behind the reset email's link.
 *
 * Deliberately does NOT sign them in afterwards, matching what the API does:
 * every session the person held has just been ended, and the sign-in screen is
 * where the new password gets proved. It also means a reset finished on a
 * borrowed computer leaves no session behind on it.
 */
export function ResetPassword({ token }: { readonly token: string }): ReactNode {
  const { client, session } = usePortal();
  const navigate = useNavigate();
  const [done, setDone] = useState<string | null>(null);

  const { state, reload } = useAsync<OpenPasswordReset>(
    () => client.openPasswordReset(token),
    [client, token],
  );

  if (done !== null) {
    return (
      <LinkCard title="Password changed">
        <div className="notice" role="status">
          <p>{done}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            // Clears whatever this browser was holding as well. The API has
            // already revoked it; leaving it in local storage would put the
            // person back into a portal every request of which now 401s.
            session.clear(null);
            navigate(href.surveys());
          }}
        >
          Go to sign in
        </button>
      </LinkCard>
    );
  }

  return (
    <LinkCard title="Choose a new password">
      <Async state={state} what="this reset link" reload={reload}>
        {(reset) => (
          <>
            <p className="small">
              This resets the password for{" "}
              <span className="mono">{reset.email}</span>. Using it signs you out
              everywhere, including on any phone you are signed in on, so you
              will sign in again with the new password.
            </p>
            <ChoosePassword
              submitLabel="Change my password"
              busyLabel="Changing…"
              onChosen={async (password) => {
                const result = await client.completePasswordReset(
                  token,
                  password,
                );
                // The API's own sentence, which says both what happened and
                // what to do next.
                setDone(result.message);
              }}
            />
          </>
        )}
      </Async>
    </LinkCard>
  );
}

/**
 * "I have forgotten my password" — the form on the sign-in card.
 *
 * The answer is the same whether or not the address names an account, and it is
 * the API's sentence rather than one of ours. That is the whole security
 * property of this form: anybody on the internet can post to it, and an endpoint
 * that answered differently for a known address would be a way to find out which
 * installers have accounts here — which is exactly what a phishing campaign
 * against this product would want to know first.
 */
export function RequestPasswordReset({
  onDone,
}: {
  /** Back to the sign-in form. */
  readonly onDone: () => void;
}): ReactNode {
  const { client } = usePortal();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await client.requestPasswordReset(email.trim());
      setSent(result.message);
    } catch (cause: unknown) {
      setError(messageFor(cause));
    } finally {
      setBusy(false);
    }
  }

  if (sent !== null) {
    return (
      <>
        <div className="notice" role="status">
          <p>{sent}</p>
        </div>
        <p className="hint small">
          Check the spam folder if it has not arrived in a few minutes. If you
          are not sure which address you used, ask an owner or an administrator
          at your company — they can see the address on your account, though not
          your password.
        </p>
        <button type="button" className="link" onClick={onDone}>
          Back to sign in
        </button>
      </>
    );
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <p className="small">
        Type the email address you sign in with and we will send a link to
        choose a new password. The link lasts an hour.
      </p>
      <div className="field">
        <label htmlFor="reset-email">Email</label>
        <input
          id="reset-email"
          name="reset-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      {error ? (
        <div className="notice fail" role="alert">
          <p>{error}</p>
        </div>
      ) : null}

      <button type="submit" disabled={busy || email.trim() === ""}>
        {busy ? "Sending…" : "Email me a reset link"}
      </button>{" "}
      <button type="button" className="link" onClick={onDone}>
        Back to sign in
      </button>
    </form>
  );
}
